import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import { canManageScrimmageStats } from '../utils/practiceSchedule';
import './ClubHome.css';

const formatPracticeDate = (dateStr) => {
  if (!dateStr) return 'TBA';
  return new Date(dateStr).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ClubCompletedPractices = () => {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [canManageClub, setCanManageClub] = useState(false);
  const [completedPractices, setCompletedPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [clubData, practicesData] = await Promise.all([
        api.getClubById(id),
        api.getCompletedPracticesByClub(id, 50),
      ]);
      setClub(clubData);
      setCompletedPractices(Array.isArray(practicesData) ? practicesData : []);

      if (user?.id) {
        try {
          const members = await api.getClubMembers(id, null, user.id);
          const myMember = members.find(
            (m) =>
              m.player?.userId === user.id ||
              (m.player && m.player.id && m.player.email === user.email)
          );
          const isPracticeCoordinator = myMember?.roles?.includes('PRACTICE_COORDINATOR');
          setCanManageClub(
            clubData.adminUserId === user.id || isAdmin() || isPracticeCoordinator
          );
        } catch {
          setCanManageClub(clubData.adminUserId === user.id || isAdmin());
        }
      } else {
        setCanManageClub(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to load completed practices');
      setCompletedPractices([]);
    } finally {
      setLoading(false);
    }
  }, [id, user, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="club-home">
        <p>Loading completed practices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="club-home">
        <p className="error-message">{error}</p>
        <Link to={soccerPath(`/clubs/${id}`)} className="btn btn-secondary">
          Back to club
        </Link>
      </div>
    );
  }

  return (
    <div className="club-home">
      <div className="club-completed-practices__header">
        <div>
          <Link to={soccerPath(`/clubs/${id}`)} className="club-completed-practices__back">
            ← Back to {club?.name || 'club'}
          </Link>
          <h1>Completed Practices</h1>
          <p>Past sessions for {club?.name || 'this club'}, most recent first.</p>
        </div>
        <Link to={soccerPath(`/clubs/${id}/practices`)} className="btn btn-secondary">
          Schedule Practices
        </Link>
      </div>

      {completedPractices.length === 0 ? (
        <p className="club-upcoming-practices__empty">No completed practices yet.</p>
      ) : (
        <div className="club-upcoming-practices__list">
          {completedPractices.map((practice) => (
            <div key={practice.id} className="club-practice-card club-practice-card--completed">
              <div className="club-practice-card__info">
                <h3>
                  {practice.title}
                  {practice.seriesId && (
                    <span className="club-practice-card__badge">Recurring</span>
                  )}
                </h3>
                <p className="club-practice-card__date">{formatPracticeDate(practice.practiceDate)}</p>
                {practice.location && (
                  <p className="club-practice-card__location">{practice.location}</p>
                )}
              </div>
              <div className="club-practice-card__actions">
                <Link
                  to={soccerPath(`/practices/${practice.id}`)}
                  className="btn btn-secondary"
                >
                  View Practice
                </Link>
                <Link
                  to={soccerPath(`/practices/${practice.id}/summary`)}
                  className="btn btn-primary"
                >
                  View Summary
                </Link>
                {canManageClub && canManageScrimmageStats(practice) && (
                  <Link
                    to={soccerPath(`/practices/${practice.id}/scrimmage-stats`)}
                    className="btn btn-secondary"
                  >
                    📊 Update scrimmage stats
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClubCompletedPractices;
